import { calcularDistancia } from '../utils/geo';
import { DEFAULT_SEARCH_RADIUS_KM, SERVICE_SEARCH_TIMEOUT_SEC } from '../constants';

export const VISIBILITY_REASONS = {
    NO_LOCATION: 'NO_LOCATION',
    EXPIRED: 'EXPIRED',
    OUT_OF_RADIUS: 'OUT_OF_RADIUS',
    VEHICLE_TYPE_MISMATCH: 'VEHICLE_TYPE_MISMATCH',
    CATEGORY_DISABLED: 'CATEGORY_DISABLED'
};

export const REASON_PRIORITY = [
    VISIBILITY_REASONS.NO_LOCATION,
    VISIBILITY_REASONS.OUT_OF_RADIUS,
    VISIBILITY_REASONS.EXPIRED,
    VISIBILITY_REASONS.VEHICLE_TYPE_MISMATCH,
    VISIBILITY_REASONS.CATEGORY_DISABLED
];

export const hasUsableLocation = (location) => {
    if (!location) return false;
    const { lat, lng } = location;
    if (lat == null || lng == null) return false;
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return false;
    return !(latNum === 0 && lngNum === 0);
};

export const getServiceDistanceKm = (service, location) => {
    const pickup = service?.pickup;
    if (!pickup || pickup.lat == null || pickup.lng == null) return null;
    if (!hasUsableLocation(location)) return null;
    return calcularDistancia(
        Number(location.lat),
        Number(location.lng),
        Number(pickup.lat),
        Number(pickup.lng)
    );
};

export const getServiceAgeSeconds = (service, now = Date.now()) => {
    if (!service?.created_at) return null;
    const createdAtMs = new Date(service.created_at).getTime();
    if (Number.isNaN(createdAtMs)) return null;
    return Math.max(0, Math.round((now - createdAtMs) / 1000));
};

export const classifyServiceVisibility = (service, ctx = {}) => {
    const {
        location = null,
        radiusKm = DEFAULT_SEARCH_RADIUS_KM,
        activeVehicleTypes = [],
        disabledSlugs = [],
        now = Date.now(),
        timeoutSec = SERVICE_SEARCH_TIMEOUT_SEC
    } = ctx;

    if (!hasUsableLocation(location)) return VISIBILITY_REASONS.NO_LOCATION;

    const ageSeconds = getServiceAgeSeconds(service, now);
    if (ageSeconds !== null && ageSeconds > timeoutSec) return VISIBILITY_REASONS.EXPIRED;

    const distanceKm = getServiceDistanceKm(service, location);
    if (distanceKm === null || distanceKm > radiusKm) return VISIBILITY_REASONS.OUT_OF_RADIUS;

    if (
        service?.vehicle_type &&
        activeVehicleTypes.length > 0 &&
        !activeVehicleTypes.includes(service.vehicle_type)
    ) {
        return VISIBILITY_REASONS.VEHICLE_TYPE_MISMATCH;
    }

    if (service?.type && disabledSlugs.includes(service.type)) {
        return VISIBILITY_REASONS.CATEGORY_DISABLED;
    }

    return null;
};

export const buildVisibilityDiagnostics = (services = [], ctx = {}) => {
    const list = Array.isArray(services) ? services : [];
    const reasons = {};
    Object.keys(VISIBILITY_REASONS).forEach((key) => {
        reasons[key] = 0;
    });

    const visibleServices = [];
    const vehicleTypesRequested = new Set();
    let nearestKm = null;
    let minOutOfRadiusKm = null;
    let newestSec = null;
    let oldestSec = null;

    const now = ctx.now ?? Date.now();

    list.forEach((service) => {
        if (service?.vehicle_type) vehicleTypesRequested.add(service.vehicle_type);

        const reason = classifyServiceVisibility(service, { ...ctx, now });
        const distanceKm = getServiceDistanceKm(service, ctx.location);

        if (distanceKm !== null && (nearestKm === null || distanceKm < nearestKm)) {
            nearestKm = distanceKm;
        }

        if (reason) {
            reasons[reason] += 1;
            if (reason === VISIBILITY_REASONS.OUT_OF_RADIUS
                && distanceKm !== null
                && (minOutOfRadiusKm === null || distanceKm < minOutOfRadiusKm)) {
                minOutOfRadiusKm = distanceKm;
            }
        } else {
            visibleServices.push(service);
        }

        const ageSeconds = getServiceAgeSeconds(service, now);
        if (ageSeconds !== null) {
            if (newestSec === null || ageSeconds < newestSec) newestSec = ageSeconds;
            if (oldestSec === null || ageSeconds > oldestSec) oldestSec = ageSeconds;
        }
    });

    return {
        total: list.length,
        visible: visibleServices.length,
        hidden: list.length - visibleServices.length,
        reasons,
        visibleServices,
        nearestKm,
        minOutOfRadiusKm,
        newestSec,
        oldestSec,
        hasLocation: hasUsableLocation(ctx.location),
        location: ctx.location || null,
        radiusKm: ctx.radiusKm ?? DEFAULT_SEARCH_RADIUS_KM,
        activeVehicleTypes: ctx.activeVehicleTypes || [],
        disabledSlugs: ctx.disabledSlugs || [],
        vehicleTypesRequested: Array.from(vehicleTypesRequested)
    };
};

export const getPrimaryReason = (diagnostics) => {
    if (!diagnostics?.reasons) return null;
    for (const reason of REASON_PRIORITY) {
        if ((diagnostics.reasons[reason] || 0) > 0) return reason;
    }
    return null;
};
